import React, { useState } from 'react';
import { Form, Input, InputNumber, Select, Row, Col, Card, Tabs, Button, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { PlusOutlined } from '@ant-design/icons';
import { type FormInstance } from 'antd/es/form';

import AgePremiumRow from './AgePremiumRow';
import ServiceTypeCard from './ServiceTypeCard';
import { type ServiceType, type ServiceItem } from '../../../types/insurancePlanTypes';
// import { currencyAmountValidator } from '@/common/components/MultipleOfValidator';
const { Option } = Select;

interface PlanCategory {
  category?: string;
}
interface PlanCategoriesSectionProps {
  form: FormInstance;
  isLoadingCountries: boolean;
  countries: { label: string; value: number }[] | undefined;
  serviceTypesOptions: { label: string; value: number }[] | undefined;
  isLoadingServiceTypes: boolean;
  serviceCategoriesOptions: { label: string; labelAr?: string; value: number }[] | undefined;
  serviceCategoryItemsByCategory: Record<number, { label: string; labelAr?: string; value: number }[]>;
  isLoadingServiceCategories: boolean;
    ageRangeConditionOptions: { label: string; value: string | number }[];
  genderOptions: { label: string; value: number }[];
    maritalStatusOptions: { label: string; value: number }[];
    currencyISOCode?: string | undefined;
}

const PlanCategoriesSection: React.FC<PlanCategoriesSectionProps> = ({
  form,
  isLoadingCountries,
  countries,
  serviceTypesOptions,
  isLoadingServiceTypes,
  serviceCategoriesOptions,
  serviceCategoryItemsByCategory,
  isLoadingServiceCategories,
  ageRangeConditionOptions,
  genderOptions,
    maritalStatusOptions,
    currencyISOCode
}) => {
  const { t } = useTranslation();
  const [activeKey, setActiveKey] = useState('0');

  const handleAddCategory = async (add: (defaultValue?: Record<string, unknown>, insertIndex?: number) => void) => {
    const planCategories = form.getFieldValue('planCategories') || [];
      const lastCategoryIndex = planCategories.length - 1;

    if (lastCategoryIndex >= 0) {
      const fieldsToValidate: (string | number)[][] = [
        ['planCategories', lastCategoryIndex, 'coverageAmount'],
        ['planCategories', lastCategoryIndex, 'mainTerritory'],
        ['planCategories', lastCategoryIndex, 'extendTerritory'],
        ['planCategories', lastCategoryIndex, 'ageRangeCondition'],
      ];

      // Add validation for Service Types and Service Items within the last category
      const serviceTypes = form.getFieldValue(['planCategories', lastCategoryIndex, 'serviceTypes']) || [];
      serviceTypes.forEach((_serviceType: ServiceType, serviceTypeIndex: number) => {
        fieldsToValidate.push(
          ['planCategories', lastCategoryIndex, 'serviceTypes', serviceTypeIndex, 'serviceTypeId'],
          ['planCategories', lastCategoryIndex, 'serviceTypes', serviceTypeIndex, 'percentage'],
          ['planCategories', lastCategoryIndex, 'serviceTypes', serviceTypeIndex, 'coShare'],
        );

        const serviceItems = form.getFieldValue(['planCategories', lastCategoryIndex, 'serviceTypes', serviceTypeIndex, 'serviceItems']) || [];
        serviceItems.forEach((_serviceItem: ServiceItem, serviceItemIndex: number) => {
          fieldsToValidate.push(
            ['planCategories', lastCategoryIndex, 'serviceTypes', serviceTypeIndex, 'serviceItems', serviceItemIndex, 'serviceCategoryId'],
            ['planCategories', lastCategoryIndex, 'serviceTypes', serviceTypeIndex, 'serviceItems', serviceItemIndex, 'serviceCategoryItemIds'],
            ['planCategories', lastCategoryIndex, 'serviceTypes', serviceTypeIndex, 'serviceItems', serviceItemIndex, 'amount'],
            ['planCategories', lastCategoryIndex, 'serviceTypes', serviceTypeIndex, 'serviceItems', serviceItemIndex, 'coShare'],
            ['planCategories', lastCategoryIndex, 'serviceTypes', serviceTypeIndex, 'serviceItems', serviceItemIndex, 'percentage'],
          );
        });
      });

      const ageRangeCondition = form.getFieldValue(['planCategories', lastCategoryIndex, 'ageRangeCondition']);
      const agePremiums = form.getFieldValue(['planCategories', lastCategoryIndex, 'agePremiums']) || [];
      agePremiums.forEach((_agePremium: unknown, premiumIndex: number) => {
        fieldsToValidate.push(
          ['planCategories', lastCategoryIndex, 'agePremiums', premiumIndex, 'ageFrom'],
          ['planCategories', lastCategoryIndex, 'agePremiums', premiumIndex, 'ageTo'],
          ['planCategories', lastCategoryIndex, 'agePremiums', premiumIndex, 'premiumAmount'],
        );

        if (ageRangeCondition === 2 || ageRangeCondition === 4) {
          fieldsToValidate.push(['planCategories', lastCategoryIndex, 'agePremiums', premiumIndex, 'gender']);
        }

        if (ageRangeCondition === 3 || ageRangeCondition === 4) {
          fieldsToValidate.push(['planCategories', lastCategoryIndex, 'agePremiums', premiumIndex, 'maritalStatus']);
        }
      });

      try {
        await form.validateFields(fieldsToValidate);
      } catch {
        notification.error({
          message: t('Validation Error'),
          description: t('Please correct all errors in the current category, including its service types and items, before adding a new one.'),
        });
        return;
      }
    }

    if (planCategories.length < 3) {
      const existingCategories = new Set(planCategories.map((c: PlanCategory) => c && c.category).filter(Boolean));
      let nextCategoryCode = 65; // 'A'
      while (existingCategories.has(String.fromCharCode(nextCategoryCode))) {
        nextCategoryCode++;
      }
      const newCategoryName = String.fromCharCode(nextCategoryCode);
      add({
        category: newCategoryName,
        ageRangeCondition: 1,
        coverageAmount: undefined,
        mainTerritory: [104],
          extendTerritory: [104],
        serviceTypes: [
          {
            serviceTypeId: undefined,
            serviceType: '',
            percentage: undefined,
            amount: undefined,
            coShare: undefined,
            applyCoShare: false,
            serviceItems: [
              {
                serviceCategoryId: undefined,
                serviceCategoryItemId: [],
                percentage: undefined,
                amount: undefined,
                coShare: undefined,
                isAmountCoverage: true,
              },
            ],
          },
        ],
        agePremiums: [
          {
            ageFrom: undefined,
            ageTo: undefined,
            gender: undefined,
            maritalStatus: undefined,
            premiumAmount: undefined,
          },
        ],
      });
      setActiveKey(String(planCategories.length));
    }
  };

  const handleAddAgePremium = async (
    categoryName: number,
    addPremium: (defaultValue?: Record<string, unknown>, insertIndex?: number) => void,
  ) => {
    const agePremiums = form.getFieldValue(['planCategories', categoryName, 'agePremiums']) || [];
    const lastPremiumIndex = agePremiums.length - 1;

    if (lastPremiumIndex >= 0) {
      const ageRangeCondition = form.getFieldValue(['planCategories', categoryName, 'ageRangeCondition']);
      const fieldsToValidate: (string | number)[][] = [
        ['planCategories', categoryName, 'agePremiums', lastPremiumIndex, 'ageFrom'],
        ['planCategories', categoryName, 'agePremiums', lastPremiumIndex, 'ageTo'],
        ['planCategories', categoryName, 'agePremiums', lastPremiumIndex, 'premiumAmount'],
      ];

      if (ageRangeCondition === 2 || ageRangeCondition === 4) {
        fieldsToValidate.push(['planCategories', categoryName, 'agePremiums', lastPremiumIndex, 'gender']);
      }

      if (ageRangeCondition === 3 || ageRangeCondition === 4) {
        fieldsToValidate.push(['planCategories', categoryName, 'agePremiums', lastPremiumIndex, 'maritalStatus']);
      }

      try {
        await form.validateFields(fieldsToValidate);
      } catch {
        notification.error({
          message: t('Validation Error'),
          description: t('Please correct the errors in the current age premium before adding a new one.'),
        });
        return;
      }
    }

    addPremium({
      ageFrom: undefined,
      ageTo: undefined,
      gender: undefined,
      maritalStatus: undefined,
      premiumAmount: undefined,
    });
  };

    const updateCoverageAmount = (
        form: FormInstance,
        categoryIndex: number
    ) => {
        const serviceTypes =
            form.getFieldValue(['planCategories', categoryIndex, 'serviceTypes']) || [];

        let total = 0;

        serviceTypes.forEach((st: any) => {
            (st.serviceItems || []).forEach((si: any) => {
                if (typeof si?.amount === 'number') {
                    total += si.amount;
                }
            });
        });

        form.setFieldsValue({
            planCategories: {
                [categoryIndex]: {
                    coverageAmount: total,
                },
            },
        });
    };

  // const handleAddService = async (
  //   categoryName: number,
  //   add: (defaultValue?: Record<string, unknown>, insertIndex?: number) => void,
  // ) => {
  //   const serviceTypes = form.getFieldValue(['planCategories', categoryName, 'serviceTypes']) || [];
  //   const lastServiceTypeIndex = serviceTypes.length - 1;

  //   if (lastServiceTypeIndex >= 0) {
  //     const fieldsToValidate: (string | number)[][] = [
  //       ['planCategories', categoryName, 'serviceTypes', lastServiceTypeIndex, 'serviceTypeId'],
  //       ['planCategories', categoryName, 'serviceTypes', lastServiceTypeIndex, 'percentage'],
  //       ['planCategories', categoryName, 'serviceTypes', lastServiceTypeIndex, 'coShare'],
  //     ];

  //     const serviceItems = form.getFieldValue(['planCategories', categoryName, 'serviceTypes', lastServiceTypeIndex, 'serviceItems']) || [];
  //     serviceItems.forEach((_serviceItem: ServiceItem, serviceItemIndex: number) => {
  //       fieldsToValidate.push(
  //         ['planCategories', categoryName, 'serviceTypes', lastServiceTypeIndex, 'serviceItems', serviceItemIndex, 'serviceCategoryId'],
  //         ['planCategories', categoryName, 'serviceTypes', lastServiceTypeIndex, 'serviceItems', serviceItemIndex, 'serviceCategoryItemId'],
  //         ['planCategories', categoryName, 'serviceTypes', lastServiceTypeIndex, 'serviceItems', serviceItemIndex, 'coShare'],
  //         ['planCategories', categoryName, 'serviceTypes', lastServiceTypeIndex, 'serviceItems', serviceItemIndex, 'percentage'],
  //       );
  //     });

  //     try {
  //       await form.validateFields(fieldsToValidate);
  //     } catch {
  //       notification.error({
  //         message: t('Validation Error'),
  //         description: t('Please correct the errors in the current service type and its items before adding a new one.'),
  //       });
  //       return;
  //     }
  //   }

  //   add({
  //     serviceTypeId: undefined,
  //     serviceType: '',
  //     percentage: undefined,
  //     amount: undefined,
  //     coShare: undefined,
  //     applyCoShare: false,
  //     serviceItems: [
  //       {
  //         serviceCategoryId: undefined,
  //         serviceCategoryItemId: undefined,
  //         percentage: undefined,
  //         amount: undefined,
  //         coShare: undefined,
  //         isAmountCoverage: false,
  //       },
  //     ],
  //   });
  // };

  return (
    <Card title={t('Plan Categories')} style={{ marginTop: 16 }}>
      <Form.List name="planCategories">
        {(fields, { add, remove }) => (
          <Tabs
            hideAdd={fields.length >= 3}
            type="editable-card"
            activeKey={activeKey}
            onChange={setActiveKey}
            onEdit={(targetKey, action) => {
              if (action === 'add') {
                handleAddCategory(add);
              } else {
                if (fields.length > 1) {
                  const targetIndex = Number(targetKey);
                  if (activeKey === targetKey) {
                    const newActiveKey = targetIndex > 0 ? String(targetIndex - 1) : '0';
                    setActiveKey(newActiveKey);
                  }
                  remove(targetIndex);
                }
              }
            }}
            items={fields.map(({ key, name: categoryName, ...restField }, index) => ({
              key: String(index),
              label: `${t('Category')} ${form.getFieldValue(['planCategories', categoryName, 'category']) || ''}`,
              closable: fields.length > 1,
              children: (
                <div key={key} style={{ marginTop: 16 }}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        {...restField}
                        name={[categoryName, 'category']}
                        label={t('Category Name (A, B, C)')}
                        rules={[{ required: true, message: t('Missing category name') }]}
                      >
                        <Input size="large" disabled />
                      </Form.Item>
                    </Col>
                    <Col span={12} >
                      <Form.Item
                        {...restField}
                        name={[categoryName, 'coverageAmount']}
                        label={t('Coverage Amount')}
                        
                        // rules={[{
                        //     required: true, message: t('Missing coverage amount'),
                        // },
                        //     currencyAmountValidator(currencyISOCode, "")
                        // ]}
                              >
                                  <InputNumber disabled={true} min={1} max={100000000} precision={0} style={{ width: '100%' }} size="large" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        {...restField}
                                  name={[categoryName, 'mainTerritory']}
                                  initialValue={[104]}
                        label={t('Main Territory')}
                        rules={[{ required: true, message: t('Please select main territories!') }]}
                      >
                        <Select
                          mode="multiple"
                          placeholder={t('Select countries')}
                          loading={isLoadingCountries}
                          size="large"
                          showSearch={true}
                          filterOption={(input, option) =>
                            String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                        >
                          {countries?.map(country => (
                            <Option key={country.value} value={country.value} label={country.label}>
                              {country.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        {...restField}
                        name={[categoryName, 'extendTerritory']}
                                  label={t('Extend Territory')}
                                  initialValue={104}
                        rules={[{ required: true, message: t('Please select extended territories!') }]}
                      >
                        <Select
                          mode="multiple"
                          placeholder={t('Select countries')}
                          loading={isLoadingCountries}
                          size="large"
                          showSearch={true}
                          filterOption={(input, option) =>
                            String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                        >
                          {countries?.map(country => (
                            <Option key={country.value} value={country.value} label={country.label}>
                              {country.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                                  {...restField}
                                  name={[categoryName, 'ageRangeCondition']}
                                  label={t('Age Range Condition')}
                                  rules={[{ required: true, message: t('Please select age range condition!') }]}
                                  hidden={true}
                      >
                        <Select
                          placeholder={t('Select condition')}
                          size="large"
                          showSearch={true}
                          filterOption={(input, option) =>
                            String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                        >
                          {ageRangeConditionOptions.map(option => (
                            <Option key={option.value} value={option.value} label={option.label}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* <Card title={t('Service Types')} style={{ marginTop: 16 }}> */}
                    <Form.List name={[categoryName, 'serviceTypes']}>
                      {(serviceFields, {  remove: removeService }) => (///add: addService,
                        <>
                          {serviceFields.map(({ key: serviceKey, name: serviceName, ...serviceRestField }) => (
                            <ServiceTypeCard
                                  key={serviceKey}
                                  form={form}
                                  categoryName={categoryName}
                                  serviceName={serviceName}
                                  serviceRestField={{ ...serviceRestField, key: serviceKey, name: serviceName }}
                                  removeService={removeService}
                                  serviceTypesOptions={serviceTypesOptions}
                                  isLoadingServiceTypes={isLoadingServiceTypes}
                                  serviceCategoriesOptions={serviceCategoriesOptions}
                                  serviceCategoryItemsByCategory={serviceCategoryItemsByCategory}
                                  isLoadingServiceCategories={isLoadingServiceCategories}
                                  currencyISOCode={currencyISOCode}
                                  updateCoverageAmount={updateCoverageAmount}
                            />
                          ))}
                          {/* <div style={{ textAlign: 'center', marginTop: 16 }}>
                            <Button
                              type="primary"
                              onClick={() => handleAddService(categoryName, addService)}
                              icon={<PlusOutlined />}
                              block
                            >
                              {t('Add Service Type')}
                            </Button>
                          </div> */}
                        </>
                      )}
                    </Form.List>
                  {/* </Card> */}

                  <Card title={t('Age Premiums')} style={{ marginTop: 16 }}>
                    <Form.List name={[categoryName, 'agePremiums']}>
                      {(premiumFields, { add: addPremium, remove: removePremium }) => (
                        <>
                          <div
                            style={{
                              background: '#fff',
                              borderRadius: 8,
                              border: '1px solid #e5e7eb',
                              overflow: 'hidden',
                              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                            }}
                          >
                            {(() => {
                              const currentAgeRangeCondition = form.getFieldValue(['planCategories', categoryName, 'ageRangeCondition']);
                              const showGenderHeader = currentAgeRangeCondition === 2 || currentAgeRangeCondition === 4;
                              const showMaritalStatusHeader = currentAgeRangeCondition === 3 || currentAgeRangeCondition === 4;
                              const headerGridTemplateColumns = [
                                '32px',
                                'minmax(120px, 1fr)',
                                'minmax(120px, 1fr)',
                                ...(showGenderHeader ? ['minmax(140px, 1fr)'] : []),
                                ...(showMaritalStatusHeader ? ['minmax(160px, 1fr)'] : []),
                                'minmax(140px, 1fr)',
                                '32px',
                              ].join(' ');

                              const headerStyle = {
                                display: 'grid',
                                gridTemplateColumns: headerGridTemplateColumns,
                                gap: 12,
                                padding: '10px 12px',
                                background: 'linear-gradient(to right, #f9fafb, #f3f4f6)',
                                borderBottom: '2px solid #e5e7eb',
                                fontSize: 12,
                                fontWeight: 600,
                                color: 'rgba(0,0,0,0.88)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                              } as const;

                              return (
                                <div style={headerStyle}>
                                  <div>#</div>
                                  <div>{t('Age From')}</div>
                                  <div>{t('Age To')}</div>
                                  {showGenderHeader ? <div>{t('Gender')}</div> : null}
                                  {showMaritalStatusHeader ? <div>{t('Marital Status')}</div> : null}
                                  <div>{t('Premium Amount')}</div>
                                  <div style={{ width: 32 }} />
                                </div>
                              );
                            })()}

                            {premiumFields.length === 0 ? (
                              <div style={{ padding: '32px 16px', textAlign: 'center' }}>
                                <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 8 }}>
                                  {t('No age premiums added yet.')}
                                </div>
                                <div style={{ fontSize: 12, color: '#d1d5db' }}>
                                  {t('Click the button below to add your first age premium')}
                                </div>
                              </div>
                            ) : (
                              premiumFields.map(({ key: premiumKey, name: premiumName, ...premiumRestField }) => (
                                <AgePremiumRow
                                  key={premiumKey}
                                  premiumKey={premiumKey}
                                  premiumName={premiumName}
                                  premiumRestField={premiumRestField}
                                      categoryName={categoryName}
                                      currencyISOCode={currencyISOCode}
                                  removePremium={removePremium}
                                  form={form}
                                  genderOptions={genderOptions}
                                  maritalStatusOptions={maritalStatusOptions}
                                />
                              ))
                            )}

                            <div style={{ padding: '12px', background: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
                              <Button
                                type="dashed"
                                onClick={() => handleAddAgePremium(categoryName, addPremium)}
                                icon={<PlusOutlined />}
                                block
                                style={{
                                  height: 36,
                                  borderColor: '#d1d5db',
                                  color: '#0891B2',
                                  fontWeight: 500,
                                }}
                              >
                                {t('Add Age Premium')}
                              </Button>
                            </div>
                          </div>
                        </>
                      )}
                    </Form.List>
                  </Card>
                </div>
              ),
            }))}
          />
        )}
      </Form.List>
    </Card>
  );
};

export default PlanCategoriesSection;